/**
 * Demo pipeline templates from docs/demo-sample-pipelines.md.
 * Used for Step 0 template picker and to prefill name + stages.
 *
 * Stage colors use the same keys as ColorPicker (e.g. "text-blue-500").
 * Semantic mapping: early = cool/neutral, middle = active, won = green, lost = red.
 */

export type DemoPipelineStage = {
  name: string;
  description: string;
  /** Color key from ColorPicker (e.g. "text-blue-500"). Used for stage badge/dot in UI. */
  color?: string;
};

/** Semantic palette: entry → middle → won/lost. Matches ColorPicker color keys. */
const STAGE_COLORS = {
  entry: "text-sky-500", // First touch (lead, trial, sourced)
  early: "text-blue-500", // Early progress
  middle: "text-teal-500", // Active engagement
  active: "text-amber-500", // Proposal, negotiation
  late: "text-indigo-500", // Contract, commitment
  won: "text-green-500", // Success (won, hired, confirmed, completed, closed)
  lost: "text-red-500", // Lost, declined, cancelled, passed
  neutral: "text-neutral-500", // Fallback / closed–no
} as const;

function stageColor(name: string, index: number, total: number): string {
  const n = name.toLowerCase();
  // Lost/closed-no first (so "closed lost" doesn’t match "closed" as won)
  if (
    n.includes("closed lost") ||
    n.includes("closed – no") ||
    n.includes("lost / defer") ||
    n === "lost" ||
    n.includes("declined") ||
    n.includes("cancelled") ||
    n.includes("passed")
  )
    return STAGE_COLORS.lost;
  if (
    n.includes("won") ||
    n === "hired" ||
    n === "confirmed" ||
    n === "completed" ||
    n === "closed" ||
    n === "active"
  )
    return STAGE_COLORS.won;
  if (index === 0) return STAGE_COLORS.entry;
  if (index >= total - 2 && total > 2) return STAGE_COLORS.late;
  const progress = index / Math.max(1, total - 1);
  if (progress < 0.33) return STAGE_COLORS.early;
  if (progress < 0.66) return STAGE_COLORS.middle;
  return STAGE_COLORS.active;
}

export type DemoPipelineTemplate = {
  id: string;
  name: string;
  useCase: string;
  stages: DemoPipelineStage[];
  /** CSV key for sample data import (e.g. pipeline-01-standard-sales) */
  csvKey: string;
  hasSampleData: boolean;
  /** Short automation/AI suggestions for Step 4 (2–3 bullets). */
  automationSuggestions: string[];
};

function withStageColors(stages: Omit<DemoPipelineStage, "color">[]): DemoPipelineStage[] {
  return stages.map((s, i) => ({ ...s, color: stageColor(s.name, i, stages.length) }));
}

export const DEMO_PIPELINE_TEMPLATES: DemoPipelineTemplate[] = [
  {
    id: "pipeline-01-standard-sales",
    name: "Revenue – Standard Sales",
    useCase: "Classic B2B sales (lead → close).",
    csvKey: "pipeline-01-standard-sales",
    hasSampleData: true,
    automationSuggestions: [
      "Notify when a deal moves to Proposal (e.g. Slack #sales).",
      "Enrich new leads (research company, classify ICP).",
      "Set closed date and notify when deal is Closed Won/Lost.",
    ],
    stages: withStageColors([
      { name: "Lead", description: "New lead, not yet qualified" },
      { name: "Qualified", description: "Qualified opportunity" },
      { name: "Proposal", description: "Proposal sent" },
      { name: "Negotiation", description: "In negotiation" },
      { name: "Closed Won", description: "Deal won" },
      { name: "Closed Lost", description: "Deal lost" },
    ]),
  },
  {
    id: "pipeline-02-saas-plg",
    name: "SaaS – PLG to Sales",
    useCase: "Free/trial → paid, sales-assisted.",
    csvKey: "pipeline-02-saas-plg",
    hasSampleData: true,
    automationSuggestions: [
      "Notify when deal → Qualified (Slack, assign).",
      "Notify on Demo Scheduled (email + Slack).",
      "Score trial fit (research, classify ICP).",
    ],
    stages: withStageColors([
      { name: "Trial", description: "Free trial / signup" },
      { name: "Activation", description: "Product activated" },
      { name: "Qualified", description: "Usage/ICP qualified" },
      { name: "Demo Scheduled", description: "Demo or call booked" },
      { name: "Proposal", description: "Proposal / quote sent" },
      { name: "Closed Won", description: "Customer" },
      { name: "Closed Lost", description: "Churned / disqualified" },
    ]),
  },
  {
    id: "pipeline-03-agency",
    name: "Agency – Project Pipeline",
    useCase: "Agencies (pitches, projects, retainers).",
    csvKey: "pipeline-03-agency",
    hasSampleData: true,
    automationSuggestions: [
      "Notify when proposal sent (Slack, email).",
      "Remind when in Negotiation (Slack).",
      "Win/loss tag on Won/Lost (classify).",
    ],
    stages: withStageColors([
      { name: "Lead", description: "Inbound or outbound lead" },
      { name: "Discovery", description: "Discovery call / scoping" },
      { name: "Proposal", description: "Proposal sent" },
      { name: "Negotiation", description: "Scope or pricing negotiation" },
      { name: "Contract Sent", description: "Contract sent" },
      { name: "Won", description: "Project / retainer won" },
      { name: "Lost", description: "Opportunity lost" },
    ]),
  },
  {
    id: "pipeline-04-partnerships",
    name: "Partnerships – Alliance Pipeline",
    useCase: "Partner deals, co-sell, referrals.",
    csvKey: "pipeline-04-partnerships",
    hasSampleData: true,
    automationSuggestions: [
      "Notify on new partner lead (Slack, assign).",
      "Notify when Signed (Slack, email).",
      "Partner fit (research, classify type).",
    ],
    stages: withStageColors([
      { name: "Lead", description: "Partner lead / intro" },
      { name: "Evaluating", description: "Evaluating fit" },
      { name: "Agreement", description: "Terms / agreement in progress" },
      { name: "Signed", description: "Partnership signed" },
      { name: "Active", description: "Live partnership" },
      { name: "Closed – No", description: "Not pursued" },
    ]),
  },
  {
    id: "pipeline-05-recruiting",
    name: "Recruiting – Candidate Pipeline",
    useCase: "Recruiting (roles as deals, candidates as progression).",
    csvKey: "pipeline-05-recruiting",
    hasSampleData: true,
    automationSuggestions: [
      "Notify on Applied (Slack, assign).",
      "Notify on Interview (email, Slack).",
      "Summarize resume (summarize, classify).",
    ],
    stages: withStageColors([
      { name: "Sourced", description: "Candidate sourced" },
      { name: "Applied", description: "Application received" },
      { name: "Screen", description: "Phone / first screen" },
      { name: "Interview", description: "On-site / panel" },
      { name: "Offer", description: "Offer extended" },
      { name: "Hired", description: "Accepted" },
      { name: "Declined", description: "Dropped / declined" },
    ]),
  },
  {
    id: "pipeline-06-real-estate",
    name: "Real Estate – Deal Pipeline",
    useCase: "Property or commercial deals.",
    csvKey: "pipeline-06-real-estate",
    hasSampleData: true,
    automationSuggestions: [
      "Notify when Viewing (Slack, email).",
      "Notify when Offer (Slack, assign).",
      "Property/area context (research, enrich).",
    ],
    stages: withStageColors([
      { name: "Lead", description: "Inquiry / lead" },
      { name: "Viewing", description: "Viewing scheduled / done" },
      { name: "Offer", description: "Offer submitted" },
      { name: "Negotiation", description: "Under negotiation" },
      { name: "Due Diligence", description: "In due diligence" },
      { name: "Closed Won", description: "Closed" },
      { name: "Closed Lost", description: "Lost" },
    ]),
  },
  {
    id: "pipeline-07-expansion",
    name: "Customer Success – Expansion",
    useCase: "In-account expansion, upsell, renewal risk.",
    csvKey: "pipeline-07-expansion",
    hasSampleData: true,
    automationSuggestions: [
      "Notify when Identified/Engaged (Slack, assign).",
      "Notify on Won (Slack, set date).",
      "Expansion reason (research, classify).",
    ],
    stages: withStageColors([
      { name: "Identified", description: "Expansion opportunity found" },
      { name: "Engaged", description: "Customer engaged" },
      { name: "Proposal", description: "Proposal / quote sent" },
      { name: "Negotiation", description: "In negotiation" },
      { name: "Won", description: "Expansion closed" },
      { name: "Lost / Defer", description: "Lost or deferred" },
    ]),
  },
  {
    id: "pipeline-08-events-sponsorship",
    name: "Events – Sponsorship Pipeline",
    useCase: "Event sponsorships, booths, speaking.",
    csvKey: "pipeline-08-events-sponsorship",
    hasSampleData: true,
    automationSuggestions: [
      "Notify on new sponsor lead (Slack, assign).",
      "Notify when Confirmed (Slack, email).",
      "Sponsor fit and tier (research, classify).",
    ],
    stages: withStageColors([
      { name: "Lead", description: "Event / sponsor lead" },
      { name: "Interested", description: "Expressed interest" },
      { name: "Proposal", description: "Proposal sent" },
      { name: "Contract", description: "Contract in progress" },
      { name: "Confirmed", description: "Sponsorship confirmed" },
      { name: "Lost", description: "Not pursuing" },
    ]),
  },
  {
    id: "pipeline-09-marketing-campaigns",
    name: "Marketing – Campaign Pipeline",
    useCase: "Campaigns or content deals (e.g. co-marketing, webinars).",
    csvKey: "pipeline-09-marketing-campaigns",
    hasSampleData: true,
    automationSuggestions: [
      "Notify when Launched (Slack, email).",
      "Notify when Completed (Slack).",
      "Campaign type/channel (classify, summarize).",
    ],
    stages: withStageColors([
      { name: "Idea", description: "Campaign idea" },
      { name: "Planned", description: "Planned / approved" },
      { name: "In Progress", description: "In execution" },
      { name: "Launched", description: "Launched" },
      { name: "Completed", description: "Completed" },
      { name: "Cancelled", description: "Cancelled / paused" },
    ]),
  },
  {
    id: "pipeline-10-deal-flow",
    name: "Deal Flow – Investments",
    useCase: "Simple deal flow (e.g. angel/VC, or internal ventures).",
    csvKey: "pipeline-10-deal-flow",
    hasSampleData: true,
    automationSuggestions: [
      "Notify on new deal (Slack, assign).",
      "Notify when Term Sheet/Committed (Slack, email).",
      "Company/round context (research, classify).",
    ],
    stages: withStageColors([
      { name: "Sourced", description: "Deal sourced" },
      { name: "First Look", description: "First review" },
      { name: "Diligence", description: "In diligence" },
      { name: "Term Sheet", description: "Term sheet / offer" },
      { name: "Committed", description: "Committed" },
      { name: "Closed", description: "Closed" },
      { name: "Passed", description: "Passed" },
    ]),
  },
];
